/**
 * Details of a single on-site sale.
 *
 * Renders entirely from the locally stored row — no live Stripe call — which is why
 * paymentMethodType and the fee figures are captured at webhook/sync time rather than fetched
 * here. Opening the drawer is instant and works even if Stripe is unreachable.
 *
 * No buyer email is shown. The flow is anonymous by design and we never store one; Stripe
 * collects an address purely to send its receipt.
 */
import React from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";

import { TwButton, TwInputGroup } from "../../components/tailwind";
import TwDrawer from "../../components/tailwind/TwDrawer";
import { sendOnsiteReceipt } from "../../utils/productsApi";

const STATUS = {
  paid: { label: "Paid", className: "bg-green-100 text-green-700" },
  refunded: { label: "Refunded", className: "bg-red-100 text-red-700" },
  partially_refunded: { label: "Partially refunded", className: "bg-amber-100 text-amber-700" },
  disputed: { label: "Disputed", className: "bg-red-100 text-red-700" },
};

const PAYMENT_METHOD_LABELS = {
  ideal: "iDEAL",
  card: "Card",
  bancontact: "Bancontact",
  sepa_debit: "SEPA Direct Debit",
  link: "Link",
};

const eur = (amount) => `€${parseFloat(amount || 0).toFixed(2)}`;

const Row = ({ label, value, strong = false }) => (
  <div className="flex items-baseline justify-between gap-4 py-1">
    <span className="text-sm text-gray-500">{label}</span>
    <span className={`text-sm tabular-nums ${strong ? "font-semibold text-gray-900" : "text-gray-900"}`}>
      {value}
    </span>
  </div>
);

const CopyableRef = ({ value }) => {
  const [copied, setCopied] = React.useState(false);
  if (!value) return null;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (e) {
      // Clipboard can be blocked; the value is visible on screen regardless.
    }
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      title="Copy"
      className="block w-full text-left font-mono text-xs text-gray-600 break-all hover:text-gray-900 py-0.5"
    >
      {copied ? "Copied" : value}
    </button>
  );
};

export default function OnsiteSaleDetailsDrawer({ open, sale, handleClose = () => {} }) {
  const { t } = useTranslation();

  const [showReceiptForm, setShowReceiptForm] = React.useState(false);
  const [email, setEmail] = React.useState("");
  const [sending, setSending] = React.useState(false);
  const [message, setMessage] = React.useState("");
  const [errors, setErrors] = React.useState({});

  React.useEffect(() => {
    if (!open) {
      setShowReceiptForm(false);
      setEmail("");
      setMessage("");
      setErrors({});
    }
  }, [open]);

  if (!sale) return null;

  const status = STATUS[sale.paymentStatus] || { label: sale.paymentStatus, className: "bg-gray-100 text-gray-700" };

  // Basket lines, when the sale has them. Empty for single-product sales and for anything
  // recorded before baskets existed, which falls back to the product snapshot below.
  const items = Array.isArray(sale.itemsBought) ? sale.itemsBought : [];
  const totalUnits = items.reduce((sum, item) => sum + (parseInt(item?.quantity) || 0), 0);
  const hasFees = Number(sale.netPayoutAmount || 0) > 0;

  const handleSend = async () => {
    const trimmed = String(email || "").trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setErrors({ email: t("onsiteSales.invalidEmail", "Enter a valid email address") });
      return;
    }
    setErrors({});
    setSending(true);
    const result = await sendOnsiteReceipt({ saleId: sale._id, email: trimmed });
    setSending(false);

    if (result.error) {
      setErrors({ email: result.message });
      return;
    }
    setMessage(t("onsiteSales.receiptSent", "Receipt sent to {{email}}", { email: trimmed }));
    setShowReceiptForm(false);
    setEmail("");
  };

  return (
    <TwDrawer
      title={t("onsiteSales.saleDetails", "Sale details")}
      classNameDialogPanel={`!max-w-md`}
      open={open}
      onClose={handleClose}
      footerChildren={
        <TwButton variant={"secondary-outline"} className={"w-1/3"} onClick={handleClose}>
          {t("common.close", "Close")}
        </TwButton>
      }
    >
      <div className="flex flex-col gap-6">
        <div>
          <div className="flex items-baseline gap-3">
            <span className="text-3xl font-semibold">{eur(sale.totalAmount)}</span>
            <span className={`text-xs px-2.5 py-1 rounded ${status.className}`}>{status.label}</span>
          </div>
          <p className="mt-1 text-sm text-gray-500">
            {sale.soldAt ? moment(sale.soldAt).format("D MMM YYYY, HH:mm") : "—"}
            {sale.saleReference ? ` · ${sale.saleReference}` : ""}
          </p>
        </div>

        {/* Every line in the order. The table collapses a basket to two items plus a count,
            so this is where the full breakdown lives. Falls back to the single-product
            snapshot for sales recorded before baskets existed. */}
        <div className="p-3 rounded-lg bg-gray-50">
          {items.length > 0 ? (
            <>
              {items.map((item, index) => {
                const quantity = parseInt(item?.quantity || 1);
                const lineTotal = item?.totalPriceForThisItem;
                return (
                  <div
                    key={`${item?.name || item?.productId || "item"}-${index}`}
                    className="flex items-baseline justify-between gap-3 py-1"
                  >
                    {/* Same "name x qty" wording as the sales table, so the drawer reads as
                        the expanded version of the row rather than a different format. */}
                    <div className="min-w-0">
                      <span className="font-medium">
                        {String(item?.name || item?.productId || "").trim() || "Item"} x {quantity}
                      </span>
                      {item?.unitAmount ? (
                        <span className="text-sm text-gray-500"> @ {eur(item.unitAmount)}</span>
                      ) : null}
                    </div>
                    {lineTotal !== undefined && lineTotal !== null ? (
                      <span className="text-sm tabular-nums shrink-0">{eur(lineTotal)}</span>
                    ) : null}
                  </div>
                );
              })}

              {items.length > 1 ? (
                <div className="flex items-baseline justify-between gap-3 pt-2 mt-1 border-t border-gray-200">
                  <span className="text-sm text-gray-500">
                    {t("onsiteSales.itemsTotal", "{{count}} items", { count: totalUnits })}
                  </span>
                  <span className="text-sm font-medium tabular-nums">{eur(sale.totalAmount)}</span>
                </div>
              ) : null}
            </>
          ) : (
            <div className="min-w-0 flex-1">
              <p className="font-medium truncate">{sale.productSnapshot?.name || "—"}</p>
              <p className="text-sm text-gray-500">
                {sale.quantity} × {eur(sale.unitAmount)}
              </p>
            </div>
          )}
        </div>

        <div>
          <p className="text-sm font-medium text-gray-500 mb-1">
            {t("onsiteSales.breakdown", "Payment breakdown")}
          </p>
          {hasFees ? (
            <>
              <Row label={t("onsiteSales.gross", "Gross paid")} value={eur(sale.totalAmount)} />
              <Row label={t("onsiteSales.stripeFee", "Stripe fee")} value={`−${eur(sale.stripeFeeAmount)}`} />
              <Row label={t("onsiteSales.platformFee", "Platform fee")} value={`−${eur(sale.platformFeeAmount)}`} />
              <div className="border-t border-gray-200 mt-1 pt-1">
                <Row label={t("onsiteSales.payout", "Your payout")} value={eur(sale.netPayoutAmount)} strong />
              </div>
            </>
          ) : (
            <p className="text-sm text-gray-500">
              {t(
                "onsiteSales.feesPending",
                "Fee details arrive from Stripe shortly after payment. Press Sync Stripe payments to fill them in."
              )}
            </p>
          )}
        </div>

        <div>
          <p className="text-sm font-medium text-gray-500 mb-1">
            {t("onsiteSales.details", "Details")}
          </p>
          <Row
            label={t("onsiteSales.soldBy", "Sold by")}
            value={sale.vendorId ? t("onsiteSales.vendor", "Vendor") : t("onsiteSales.you", "You")}
          />
          <Row
            label={t("onsiteSales.paymentMethod", "Payment method")}
            value={PAYMENT_METHOD_LABELS[sale.paymentMethodType] || sale.paymentMethodType || "—"}
          />
          <Row
            label={t("onsiteSales.recordedVia", "Recorded via")}
            value={sale.source === "sync" ? t("onsiteSales.sync", "Sync") : t("onsiteSales.webhook", "Webhook")}
          />
        </div>

        <div>
          <p className="text-sm font-medium text-gray-500 mb-1">
            {t("onsiteSales.paymentReferences", "Payment references")}
          </p>
          <CopyableRef value={sale.stripePaymentIntentId} />
          <CopyableRef value={sale.stripeChargeId} />
        </div>

        <div className="border-t border-gray-200 pt-4">
          {message ? <p className="text-sm text-green-700 mb-2">{message}</p> : null}

          {showReceiptForm ? (
            <div className="flex flex-col gap-3">
              <TwInputGroup
                label={t("onsiteSales.sendReceiptTo", "Send receipt to")}
                placeholder="name@example.com"
                name="email"
                type="email"
                value={email}
                errors={errors}
                onChange={(e) => setEmail(e.target.value)}
                helperText={t(
                  "onsiteSales.sendReceiptHelper",
                  "Stripe only sends a receipt when the address changes, so use a different address from the one the buyer entered."
                )}
              />
              <div className="flex gap-2">
                <TwButton variant="primary-outline" onClick={() => setShowReceiptForm(false)}>
                  {t("common.cancel", "Cancel")}
                </TwButton>
                <TwButton onClick={handleSend} actionLoading={sending} disabled={sending}>
                  {t("onsiteSales.send", "Send")}
                </TwButton>
              </div>
            </div>
          ) : (
            <TwButton variant="primary-outline" onClick={() => setShowReceiptForm(true)}>
              {t("onsiteSales.sendReceipt", "Send receipt")}
            </TwButton>
          )}
        </div>
      </div>
    </TwDrawer>
  );
}
